export function createProgram(
  gl: WebGL2RenderingContext,
  vertexSource: string,
  fragmentSource: string,
  transformFeedbackVaryings?: readonly string[],
): WebGLProgram {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  let fragment: WebGLShader;
  try {
    fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  } catch (error) {
    gl.deleteShader(vertex);
    throw error;
  }
  const program = gl.createProgram();
  if (program === null) {
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    throw new Error('WebGL2 could not allocate a shader program.');
  }
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  if (transformFeedbackVaryings !== undefined) {
    gl.transformFeedbackVaryings(
      program,
      [...transformFeedbackVaryings],
      gl.INTERLEAVED_ATTRIBS,
    );
  }
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const reason = gl.getProgramInfoLog(program) ?? 'Unknown shader link error.';
    gl.deleteProgram(program);
    throw new Error(reason);
  }
  return program;
}

export function requireUniform(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
): WebGLUniformLocation {
  const location = gl.getUniformLocation(program, name);
  if (location === null) {
    throw new Error(`Shader uniform ${name} was optimized out or not found.`);
  }
  return location;
}

export function createVertexArray(
  gl: WebGL2RenderingContext,
): WebGLVertexArrayObject {
  const vertexArray = gl.createVertexArray();
  if (vertexArray === null) {
    throw new Error('WebGL2 could not allocate a vertex array.');
  }
  return vertexArray;
}

export function createBuffer(gl: WebGL2RenderingContext): WebGLBuffer {
  const buffer = gl.createBuffer();
  if (buffer === null) {
    throw new Error('WebGL2 could not allocate a buffer.');
  }
  return buffer;
}

function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
): WebGLShader {
  const shader = gl.createShader(type);
  if (shader === null) {
    throw new Error('WebGL2 could not allocate a shader.');
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const reason = gl.getShaderInfoLog(shader) ?? 'Unknown shader compile error.';
    gl.deleteShader(shader);
    throw new Error(reason);
  }
  return shader;
}
